import { Trans, useLingui } from "@lingui/react/macro";
import { isHTTPError } from "ky";
import { SyntheticEvent, useCallback, useState } from "react";
import { useRouter } from "router2";
import useSWRMutation from "swr/mutation";

import { usePasskey } from "~/hook/domain/usePasskey.ts";
import { getPushNotificationSubscription } from "~/service/util.ts";
import { validator } from "~/service/validator.ts";
import { WebLayout } from "~/ui/layout/WebLayout.tsx";
import { useAuthNavigator, useUser } from "~/ui/provider/Auth.tsx";
import { useNotification } from "~/ui/provider/Notification.tsx";

const cx = {
  formItem: "flex flex-col gap-2 h-32 duration-300",
  helper: "text-sm text-zinc-600 dark:text-zinc-400",
  input:
    "border border-zinc-200 dark:border-zinc-800 rounded-sm bg-transparent text-zinc-800 dark:text-zinc-100 p-2 focus:outline-hidden",
  label: "text-lg",
};

interface Form {
  email: string;
  name: string;
  password: string;
}
const stepFieldNameMap = {
  1: "email",
  2: "name",
  3: "password",
} as const;

export const WebRegister = () => {
  useAuthNavigator({ goToApp: "/web/search" });
  const pushNotification = useNotification();
  const { t } = useLingui();
  const { params } = useRouter();
  const { client, patchUser, registerToken } = useUser();
  const { register: registerPasskey } = usePasskey();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const { isMutating, trigger } = useSWRMutation(
    "user/register",
    (api, { arg }: { arg: Form & { referrerId: null | number } }) =>
      client
        .post(api, { json: arg })
        .text()
        .then((token) => registerToken(token))
        .then(({ id: userId, token }) => {
          getPushNotificationSubscription()
            .then((pushSubscription) => patchUser({ pushSubscription }, token))
            .then(() => {
              pushNotification({ content: t`이제 콕 찔리면 알림이 울립니다.` });
            })
            .catch(console.error);
          registerPasskey({ token, useAutoRegister: true, userId })
            .then(() => {
              pushNotification({ content: t`Passkey가 등록되었습니다.` });
            })
            .catch(console.error);
        }),
    {
      onError: (err: unknown) => {
        switch (isHTTPError(err) && err.response.status) {
          case 409:
            pushNotification({ content: t`이미 사용중인 아이디입니다.` });
            break;
          default:
            pushNotification({ content: t`다시 시도해주세요.` });
            break;
        }
      },
      throwOnError: false,
    },
  );
  const [data, setData] = useState({
    email: "",
    name: "",
    password: "",
  });

  const onChange = useCallback(
    (key: keyof Form) => (e: { target: { value: string } }) => {
      setData((p) => ({ ...p, [key]: e.target.value }));
    },
    [],
  );

  const onSubmit = () => {
    if (step < 3) {
      const nextStep = (step + 1) as 1;
      setStep(nextStep);
      document.getElementById(stepFieldNameMap[nextStep])?.focus();
    } else {
      void trigger({
        ...data,
        referrerId: params.tag ? Number(params.tag) : null,
      });
    }
  };

  const currentKey = stepFieldNameMap[step];
  const currentFieldError = validator[currentKey](data[currentKey]);
  const hasError = currentFieldError !== null;
  const translatedCurrentFieldError = hasError && t(currentFieldError);

  return (
    <WebLayout>
      <div className="mx-auto max-w-md pt-8">
        <h1 className="mb-8 text-2xl font-bold text-zinc-800 dark:text-zinc-100">
          <Trans>회원가입</Trans>
        </h1>
        <form
          className="flex flex-col duration-300"
          data-testid="회원가입 폼"
          id="register"
          onSubmit={(e: SyntheticEvent) => {
            e.preventDefault();
            onSubmit();
          }}
          style={{ transform: `translateY(${String((step - 3) * 128)}px)` }}
        >
          <div
            aria-hidden={step < 3}
            className={cx.formItem}
            style={step < 3 ? { opacity: 0, pointerEvents: "none" } : undefined}
          >
            <label className={cx.label} htmlFor="password">
              <Trans>비밀번호</Trans>
            </label>
            <input
              className={cx.input}
              data-testid="비밀번호"
              disabled={isMutating}
              id="password"
              name="password"
              onChange={onChange("password")}
              onFocus={() => {
                setStep(3);
              }}
              type="password"
            />
            {step === 3 && hasError && (
              <p className={cx.helper}>{translatedCurrentFieldError}</p>
            )}
          </div>
          <div
            aria-hidden={step < 2}
            className={cx.formItem}
            style={step < 2 ? { opacity: 0, pointerEvents: "none" } : undefined}
          >
            <label className={cx.label} htmlFor="name">
              <Trans>이름</Trans>
            </label>
            <input
              className={cx.input}
              data-testid="이름"
              disabled={isMutating}
              id="name"
              name="name"
              onChange={onChange("name")}
              onFocus={() => {
                setStep(2);
              }}
              type="text"
            />
            {step === 2 && hasError && (
              <p className={cx.helper}>{translatedCurrentFieldError}</p>
            )}
          </div>
          <div className={cx.formItem}>
            <label className={cx.label} htmlFor="email">
              <Trans>아이디</Trans>
            </label>
            <input
              autoCapitalize="off"
              className={cx.input}
              data-testid="이메일"
              disabled={isMutating}
              id="email"
              name="email"
              onChange={onChange("email")}
              onFocus={() => {
                setStep(1);
              }}
              type="text"
            />
            {step === 1 && hasError && (
              <p className={cx.helper}>{translatedCurrentFieldError}</p>
            )}
          </div>
        </form>
        <div className="mt-8">
          <button
            className="block h-12 w-full rounded-2xl bg-black text-white duration-300 active:opacity-60 disabled:bg-zinc-300 dark:bg-zinc-800 dark:disabled:bg-zinc-900"
            data-testid="회원가입 버튼"
            disabled={isMutating || hasError}
            form="register"
            onClick={onSubmit}
            type="button"
          >
            {step === 3 ? t`회원가입` : t`다음`}
          </button>
        </div>
      </div>
    </WebLayout>
  );
};
